import type { Option } from '@app/src/components/Form/Select/SimpleSelect'
import { Alert, CircularProgress, Grid, Stack } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import RiskIndexFilterBar from '../RiskIndexFilterBar'
import type { RiskIndexFilterState, TopicFilter } from '../riskIndexFilters'
import { filterTopicCards, NO_RISK_INDEX_FILTERS } from '../riskIndexFilters'
import type { TopicCard } from './riskIndexCards'
import { sortTopicCards } from './riskIndexCards'
import RiskIndexTopicCard from './RiskIndexTopicCard'

interface RiskIndicesCardsProps {
  cards: TopicCard[]
  isEditMode: boolean
  isLoading: boolean
  isError: boolean
  onToggleIndex: (riskIndexId: number, isActivated: boolean) => void
  onHowThisWorks: () => void
}

const RiskIndicesCards: React.FC<RiskIndicesCardsProps> = ({
  cards,
  isEditMode,
  isLoading,
  isError,
  onToggleIndex,
  onHowThisWorks,
}) => {
  const { formatMessage } = useIntl()
  const [filters, setFilters] = useState<RiskIndexFilterState>(NO_RISK_INDEX_FILTERS)

  const topicOptions = useMemo<Option<TopicFilter>[]>(
    () =>
      cards.map(card => ({ value: card.topicId, label: card.title })).sort((a, b) => a.label.localeCompare(b.label)),
    [cards],
  )

  const visibleCards = sortTopicCards(filterTopicCards(cards, filters))

  return (
    <>
      <RiskIndexFilterBar filters={filters} onChange={setFilters} topicOptions={topicOptions} />

      {isLoading ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      ) : isError ? (
        <Alert severity="error">{formatMessage({ id: 'notifications.toggleRiskIndexFailed' })}</Alert>
      ) : (
        <Grid container spacing={2}>
          {visibleCards.map(card => (
            <Grid item xs={12} md={6} lg={4} key={card.key}>
              <RiskIndexTopicCard
                card={card}
                isEditMode={isEditMode}
                onToggleIndex={onToggleIndex}
                onHowThisWorks={onHowThisWorks}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  )
}

export default RiskIndicesCards
